'use client';

/**
 * Root-level error boundary.
 *
 * Next.js routes unhandled errors that escape every other error.tsx up to
 * `app/global-error.tsx`. This file replaces the default Next blank page
 * with a minimal recovery UI and forwards the error to Sentry.
 *
 * Also listens for `unhandledrejection` on the window so async errors that
 * never enter React's render cycle (e.g. background fetches in a useEffect
 * that fire-and-forget) are still reported. The toast is intentionally tiny
 * so this file stays minimal — for richer UI use the per-segment error.tsx.
 *
 * i18n note: strings here are intentionally hardcoded in English. This file
 * renders *outside* `NextIntlClientProvider` (it replaces the whole document
 * tree, including the layout that mounts the provider), so `useTranslations`
 * would throw. The English fallback is universally readable; the user is
 * already in a broken state when this surfaces.
 */
/* eslint-disable i18next/no-literal-string */

import { useEffect } from 'react';
import {
  reportBoundaryError,
  useBoundaryErrorReport,
} from '@/lib/observability/report-boundary-error';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useBoundaryErrorReport('global', error);

  useEffect(() => {
    // Forward unhandled promise rejections so RSC 503s and other background
    // async failures don't disappear silently in production. Through the same
    // helper as a boundary hit: Sentry-only was the #874 defect, and a
    // rejection nobody sees is no better than a crash nobody sees.
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      reportBoundaryError(
        'global/unhandledrejection',
        reason instanceof Error ? reason : new Error(String(reason ?? 'Unhandled rejection')),
      );
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          padding: '24px',
          fontFamily: 'system-ui, sans-serif',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fafafa',
          color: '#1a1a1a',
        }}
      >
        <div
          role="alert"
          aria-live="assertive"
          style={{
            maxWidth: '420px',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            textAlign: 'center',
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600 }}>
            Something went wrong
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
            We&rsquo;ve been notified. Try reloading the page.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              minHeight: '44px',
              padding: '0 20px',
              background: '#1a1a1a',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
